import { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import isWeb from '~/client/utils/isWeb';
import { BASE_URL } from '~/client/utils/constants';
import {
  PageMetadata,
  renderPageTitle,
  SITE_NAME,
} from '~/client/utils/pageMetadata';

// the server renders a page's title and Open Graph tags into its HTML, but
// navigating within the app never reloads that HTML, so without this they'd
// keep describing whichever page was loaded first.

const OPEN_GRAPH_PROPERTIES = [
  'og:title',
  'og:description',
  'og:image',
  'og:type',
  'og:url',
  'og:site_name',
];

// the screen whose metadata is currently in the page, so a screen losing
// focus only clears its own tags, never ones a newly focused screen just set.
let currentOwner: symbol | null = null;

function setMetaTag(
  attribute: 'property' | 'name',
  key: string,
  content?: string,
) {
  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!content) {
    tag?.remove();
    return;
  }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function applyMetadata(pageTitle?: string, metadata?: PageMetadata | null) {
  document.title = renderPageTitle(pageTitle);

  const content: Record<string, string | undefined> = metadata
    ? {
        'og:title': metadata.title,
        'og:description': metadata.description,
        'og:image': metadata.imageUrl,
        'og:type': metadata.type,
        'og:url': `${BASE_URL}${window.location.pathname}${window.location.search}`,
        'og:site_name': SITE_NAME,
      }
    : {};
  for (const property of OPEN_GRAPH_PROPERTIES) {
    setMetaTag('property', property, content[property]);
  }
  setMetaTag('name', 'twitter:card', metadata ? 'summary' : undefined);
}

// keeps the page's title and Open Graph tags in sync with the focused screen,
// matching what the server renders for the same URL. pass `null` metadata
// while the screen's data is still loading.
export default function usePageMetadata(
  pageTitle: string | undefined,
  metadata: PageMetadata | null,
) {
  const metadataKey = JSON.stringify(metadata);

  useFocusEffect(
    useCallback(() => {
      if (!isWeb()) {
        return;
      }

      const owner = Symbol('page metadata');
      currentOwner = owner;
      applyMetadata(pageTitle, metadata);

      return () => {
        if (currentOwner === owner) {
          currentOwner = null;
          applyMetadata(undefined, null);
        }
      };
      // metadata is compared by value, through metadataKey.
    }, [pageTitle, metadataKey]),
  );
}
